import React from 'react';
import {
  NodeKey,
  DecoratorNode,
  $applyNodeReplacement,
  LexicalNode,
  SerializedLexicalNode,
  Spread,
} from 'lexical';
import { Mermaid } from './Mermaid';

export type SerializedMermaidNode = Spread<
  { chart: string },
  SerializedLexicalNode
>;

/**
 * Block decorator node for a ```mermaid fenced code block. Stores the raw
 * mermaid source and renders it as a themed diagram (read-only). The source is
 * round-tripped back to a ```mermaid fence on save via the MERMAID transformer,
 * so the underlying markdown stored on the ClinicalImpression is unchanged.
 */
export class MermaidNode extends DecoratorNode<React.ReactNode> {
  __chart: string;

  static getType() {
    return 'mermaid';
  }

  static clone(node: MermaidNode) {
    return new MermaidNode(node.__chart, node.__key);
  }

  constructor(chart: string, key?: NodeKey) {
    super(key);
    this.__chart = chart;
  }

  getChart(): string {
    return this.getLatest().__chart;
  }

  createDOM() {
    const div = document.createElement('div');
    div.setAttribute('data-lexical-mermaid', 'true');
    return div;
  }

  updateDOM() {
    return false;
  }

  decorate() {
    return <Mermaid chart={this.__chart} />;
  }

  isInline(): boolean {
    return false;
  }

  static importJSON(serializedNode: SerializedMermaidNode): MermaidNode {
    return $applyNodeReplacement(
      new MermaidNode(serializedNode.chart),
    ) as MermaidNode;
  }

  exportJSON(): SerializedMermaidNode {
    return {
      ...super.exportJSON(),
      chart: this.__chart,
      type: 'mermaid',
      version: 1,
    };
  }
}

export const $createMermaidNode = (chart: string) => {
  return $applyNodeReplacement(new MermaidNode(chart));
};

export const $isMermaidNode = (
  node: LexicalNode | null | undefined,
): node is MermaidNode => {
  return node instanceof MermaidNode;
};
